/***
 *
 *   NAV ACCESS
 *   Which optional left-nav items the signed-in account may see, from one
 *   GET /api/direct-verification/me:
 *   - screening (feature 011): accounts StackGo ops have enabled for manual
 *     screening that are entitled to at least one check
 *   - workflows: accounts ops have switched the Workflows nav item on for,
 *     when the viewer is an owner, admin or master (the API decides
 *     workflow_link). The page itself is open to every account.
 *   Navigation convenience only — the Screening and Workflows pages and their
 *   APIs re-check on every request.
 *
 *   The app layout remounts on every route change, so the answer is cached per
 *   sign-in token (switching account issues a new token) and fetched quietly:
 *   a failed lookup simply hides the items rather than raising an error, and
 *   is not cached, so a later mount tries again.
 *
 **********/

import { useEffect, useState } from "react";
import Axios from "axios";

const HIDDEN = { screening: false, workflows: false };

let cache = null; // { token, access }

function currentToken() {
  try {
    return JSON.parse(localStorage.getItem("user"))?.token || null;
  } catch (e) {
    return null;
  }
}

function accessFrom(data) {
  const screening = data?.manual_screening;
  return {
    screening: screening?.sanctions === true || screening?.adverse_media === true,
    workflows: data?.workflow_link === true,
  };
}

export function useNavAccess() {
  const token = currentToken();
  const cached = cache && cache.token === token ? cache.access : null;
  const [access, setAccess] = useState(cached || HIDDEN);

  useEffect(() => {
    if (!token) return undefined;
    if (cache && cache.token === token) {
      setAccess(cache.access);
      return undefined;
    }

    let live = true;
    Axios.get("/api/direct-verification/me")
      .then((res) => {
        const value = accessFrom(res?.data?.data);
        cache = { token, access: value };
        if (live) setAccess(value);
      })
      .catch(() => {
        if (live) setAccess(HIDDEN);
      });

    return () => {
      live = false;
    };
  }, [token]);

  return access;
}
